import { setUserProfile } from "features/user/userSlice";
import { useCallback, useEffect, useRef } from "react";
import { useAuth } from "react-oidc-context";
import { useDispatch } from "react-redux";
import { setAccessToken, getAccessToken } from "../services/authTokenStore";
import { logoutUser } from "./logoutHelper";


export function AuthBridge() {
  const auth = useAuth();
  const dispatch = useDispatch();
  const retryCountRef = useRef(0);
  const lastProactiveRenew = useRef(0);
  const MAX_RETRIES = 3;
  const RETRY_DELAY = 2000;
  const PROACTIVE_RENEW_INTERVAL = 60000; // 1 minute
  const RENEW_THRESHOLD_MS = 1 * 60 * 1000; // Renew if <1min left
  const PROACTIVE_RENEW_COOLDOWN_MS = 10000; // 10 seconds

  useEffect(() => {
    const currentToken = getAccessToken();
    const newToken = auth.user?.access_token;

    if (newToken && currentToken !== newToken) {
      setAccessToken(newToken);
    }

    // Only update Redux if profile actually changed (assumes userSlice checks for deep equality)
    if (auth.user?.profile) {
      dispatch(setUserProfile(auth.user.profile));
    }

  }, [auth.user?.access_token, auth.user?.profile]);

  const handleUserLoaded = useCallback((user) => {
    console.log("Auth> Token renewed", user);
    retryCountRef.current = 0;
  }, []);

  const trySilentRenew = useCallback(() => {
    if (retryCountRef.current >= MAX_RETRIES) {
      console.error("Auth> Silent renew failed after retries, logging out");
      logoutUser({ auth });
      return;
    }

    retryCountRef.current++;
    auth.signinSilent()
      .then(user => {
        console.log("Auth> Silent renew retry succeeded", user);
      })
      .catch(error => {
        console.warn(`Auth> Silent renew retry #${retryCountRef.current} failed`, error);
        setTimeout(trySilentRenew, RETRY_DELAY);
      });
  }, [auth]);

  const handleSilentRenewError = useCallback((err) => {
    console.error("Auth> Silent renew failed", err);
    trySilentRenew();
  }, [trySilentRenew]);

  useEffect(() => {
    const interval = setInterval(() => {
      const user = auth.user;
      if (!user?.expires_at) return;

      const now = Date.now();
      const timeLeft = user.expires_at * 1000 - now;

      if (
        timeLeft < RENEW_THRESHOLD_MS &&
        now - lastProactiveRenew.current > PROACTIVE_RENEW_COOLDOWN_MS
      ) {

        lastProactiveRenew.current = now;

        console.info("Auth> Proactive silent renew");
        auth.signinSilent().catch(err => {
          console.warn("Auth> Proactive silent renew failed", err);
        });
      }
    }, PROACTIVE_RENEW_INTERVAL);

    return () => clearInterval(interval);
  }, [auth]);

  useEffect(() => {
    auth.events.addUserLoaded(handleUserLoaded);
    auth.events.addSilentRenewError(handleSilentRenewError);

    return () => {
      auth.events.removeUserLoaded(handleUserLoaded);
      auth.events.removeSilentRenewError(handleSilentRenewError);
    };
  }, [auth, handleUserLoaded, handleSilentRenewError]);

  return null;
}
